"use client";

import {
  PlayCircleOutlined,
  PauseCircleOutlined,
  RedoOutlined,
  ForwardOutlined,
  BackwardOutlined,
  StepForwardOutlined,
  StepBackwardOutlined,
  CloseOutlined,
  StopOutlined,
  SoundOutlined,
  UnorderedListOutlined,
  HeartOutlined,
  HeartFilled,
  DownloadOutlined,
  ReadOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { type FC, useState, useEffect } from "react";
import { Slider, Modal, List, Avatar, Button, Tooltip, Badge } from "antd";
import { useAudio } from "../../context/AudioProvider";
import "./shared-Comp.css";
import { useDownloadMusic } from "../../context/DownloadAudioProvider";

const AudioBookPlayerBigNew: FC = () => {
  const {
    isPlaying,
    togglePlay,
    currentTime,
    duration,
    seekAudio,
    speed,
    changeSpeed,
    setPlaybackRate,
    rewindAudio,
    forwardAudio,
    prevAudio,
    nextAudio,
    isRepeat,
    toggleRepeat,
    audioState,
    setMusicList,
    setAudioSource,
    currentMusic,
    volume,
    setVolume,
    audioRef,
    musicList,
  } = useAudio();

  const {
    currentlyDownloading,
    downloadStates,
    downloadHistory,
    downloadMusic,
    cancelDownload,
    clearDownload,
  } = useDownloadMusic();

  const navigate = useNavigate();
  const [showVolumeSlider, setShowVolumeSlider] = useState(false);
  const [showSpeedOptions, setShowSpeedOptions] = useState(false);
  const [isFavorite, setIsFavorite] = useState(false);
  const [progress, setProgress] = useState(0);
  const [showTracksModal, setShowTracksModal] = useState(false);
  const [showReadModal, setShowReadModal] = useState(false);

  // Get current track index and total tracks
  const currentTrackIndex = musicList.findIndex(
    (track) => track.musicFile === currentMusic?.musicFile
  );
  const totalTracks = musicList.length;

  // Update progress percentage for visual indicator
  useEffect(() => {
    if (duration > 0) {
      setProgress((currentTime / duration) * 100);
    }
  }, [currentTime, duration]);

  // Test
  useEffect(() => {
    console.log(currentlyDownloading);
    console.log(downloadStates);
  }, [currentlyDownloading]);

  // Format time for display (MM:SS)
  const formatTime = (seconds: number) => {
    if (isNaN(seconds)) return "00:00";
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs < 10 ? "0" : ""}${secs}`;
  };

  // Handle stop button click
  const handleStop = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      seekAudio(0);
    }
  };

  // Toggle favorite status
  const toggleFavorite = () => {
    setIsFavorite(!isFavorite);
  };

  // Handle download
  const handleDownload = () => {
    if (currentMusic !== null) {
      downloadMusic(currentMusic);
    }

    // if (currentMusic?.musicFile) {
    //   const link = document.createElement("a");
    //   link.href = currentMusic.musicFile;
    //   link.download = `${currentMusic.title || "audiobook"}.mp3`;
    //   document.body.appendChild(link);
    //   link.click();
    //   document.body.removeChild(link);
    // }
  };

  // Handle track selection from modal
  const handleTrackSelect = (track: any) => {
    if (track.musicFile) {
      setAudioSource(track.musicFile);
      setShowTracksModal(false);
    }
  };

  // Available playback speeds
  const speedOptions = [0.5, 0.75, 1.0, 1.25, 1.5, 2.0];

  return (
    <div className="flex flex-col items-center justify-center p-6 bg-amber-950 text-white min-h-screen">
      {/* Close button */}
      <button
        onClick={() => navigate(-1)}
        className="absolute top-12 left-4 text-xl text-white hover:text-gray-300 transition-colors"
        aria-label="Close player"
      >
        <CloseOutlined />
      </button>

      {/* Tracks list button */}
      <button
        onClick={() => setShowTracksModal(true)}
        className="absolute top-12 right-4 text-xl text-white hover:text-gray-300 transition-colors"
        aria-label="Show tracks"
      >
        <Badge count={totalTracks} size="small">
          <UnorderedListOutlined className=" text-2xl text-white" />
        </Badge>
      </button>

      <button
        onClick={() => setShowReadModal(true)}
        className="absolute top-20 right-4 text-xl text-white hover:text-gray-300 transition-colors"
        aria-label="Read Track"
      >
        <ReadOutlined className=" text-2xl text-white" />
      </button>

      {/* Book cover and info */}
      <div className="w-full max-w-md p-4 rounded-lg">
        <div className="relative flex justify-center mb-6">
          <img
            src={currentMusic?.coverImage || "/images/homePage/book2.svg"}
            alt={currentMusic?.title || "Audio Book"}
            className="w-1/2 max-w-[400px] rounded-lg shadow-lg"
          />

          {/* Favorite and download buttons */}
          <div className="absolute top-2 right-2 flex flex-col gap-2">
            <Tooltip
              title={isFavorite ? "Remove from favorites" : "Add to favorites"}
            >
              <button
                onClick={toggleFavorite}
                className="bg-white/20 backdrop-blur-sm p-2 rounded-full hover:bg-white/30 transition-colors"
              >
                {isFavorite ? (
                  <HeartFilled
                    style={{ fontSize: 20 }}
                    className="text-red-500"
                  />
                ) : (
                  <HeartOutlined style={{ fontSize: 20 }} />
                )}
              </button>
            </Tooltip>

            <Tooltip title="Download audio">
              <button
                onClick={handleDownload}
                className="bg-white/20 backdrop-blur-sm p-2 rounded-full hover:bg-white/30 transition-colors"
              >
                <DownloadOutlined style={{ fontSize: 20 }} />
              </button>
            </Tooltip>
          </div>
        </div>

        {/* Book title and author */}
        <h2 className="text-xl font-semibold mt-4 text-center">
          {currentMusic?.title || "No Title"}
        </h2>
        <p className="text-sm text-gray-300 text-center mb-1">
          {currentMusic?.artist || "UnKnown Author"}
        </p>

        {/* Track number indicator */}
        <p className="text-xs text-gray-400 text-center mb-4">
          Track {currentTrackIndex + 1} of {totalTracks}
        </p>

        {/* Book description */}
        {/* <p className="text-sm text-gray-300 mb-6 text-center line-clamp-3">
                    {currentMusic?.description ||
                        "---"}
                </p> */}

        {/* Progress bar with visual indicator */}
        <div className="flex justify-between text-xs mb-1">
          <span>{formatTime(currentTime)}</span>
          <span>{formatTime(duration)}</span>
        </div>
        <div className="relative w-full h-2 bg-gray-700 rounded-full mb-4">
          <div
            className="absolute top-0 left-0 h-full bg-white rounded-full transition-all duration-300 ease-out"
            style={{ width: `${progress}%` }}
          ></div>
          <input
            type="range"
            min="0"
            max={duration || 100}
            value={currentTime}
            onChange={(e) => seekAudio(Number.parseFloat(e.target.value))}
            className="absolute top-0 left-0 w-full h-full opacity-0 cursor-pointer"
          />
        </div>
      </div>

      {/* Controls */}
      <div className="flex flex-col items-center space-y-6 mt-4">
        {/* Main controls */}
        <div className="flex items-center justify-center space-x-8">
          <button
            onClick={prevAudio}
            className="text-white hover:text-gray-300 transition-colors"
            aria-label="Previous track"
          >
            <StepBackwardOutlined style={{ fontSize: 28 }} />
          </button>

          <button
            onClick={rewindAudio}
            className="text-white hover:text-gray-300 transition-colors"
            aria-label="Rewind 10 seconds"
          >
            <BackwardOutlined style={{ fontSize: 28 }} />
          </button>

          <button
            onClick={togglePlay}
            className="w-16 h-16 bg-white text-amber-950 rounded-full flex items-center justify-center hover:bg-gray-200 transition-colors"
            aria-label={isPlaying ? "Pause" : "Play"}
          >
            {isPlaying ? (
              <PauseCircleOutlined style={{ fontSize: 36 }} />
            ) : (
              <PlayCircleOutlined style={{ fontSize: 36 }} />
            )}
          </button>

          <button
            onClick={forwardAudio}
            className="text-white hover:text-gray-300 transition-colors"
            aria-label="Forward 10 seconds"
          >
            <ForwardOutlined style={{ fontSize: 28 }} />
          </button>

          <button
            onClick={nextAudio}
            className="text-white hover:text-gray-300 transition-colors"
            aria-label="Next track"
          >
            <StepForwardOutlined style={{ fontSize: 28 }} />
          </button>
        </div>

        {/* Secondary controls */}
        <div className="flex items-center justify-center space-x-8">
          {/* Stop button */}
          <button
            onClick={handleStop}
            className="text-white hover:text-gray-300 transition-colors"
            aria-label="Stop"
          >
            <StopOutlined style={{ fontSize: 24 }} />
          </button>

          {/* Repeat button */}
          <button
            onClick={toggleRepeat}
            className={`hover:text-gray-300 transition-colors ${isRepeat ? "text-green-400" : "text-white"
              }`}
            aria-label={isRepeat ? "Repeat on" : "Repeat off"}
          >
            <RedoOutlined style={{ fontSize: 24 }} />
          </button>

          {/* Speed control */}
          <div className="relative">
            <button
              onClick={() => setShowSpeedOptions(!showSpeedOptions)}
              className="text-white hover:text-gray-300 transition-colors"
              aria-label="Playback speed"
            >
              <span className="text-lg font-medium">{speed}x</span>
            </button>

            {showSpeedOptions && (
              <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 bg-gray-800 rounded-lg p-2 w-32 z-10">
                <div className="flex flex-col gap-1">
                  {speedOptions.map((rate) => (
                    <button
                      key={rate}
                      onClick={() => {
                        setPlaybackRate(rate);
                        setShowSpeedOptions(false);
                      }}
                      className={`px-2 py-1 rounded ${speed === rate
                          ? "bg-amber-700 text-white"
                          : "text-white hover:bg-gray-700"
                        }`}
                    >
                      {rate}x
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Volume control */}
          <div className="relative">
            <button
              onClick={() => setShowVolumeSlider(!showVolumeSlider)}
              className="text-white hover:text-gray-300 transition-colors"
              aria-label="Volume"
            >
              <SoundOutlined style={{ fontSize: 24 }} />
            </button>

            {showVolumeSlider && (
              <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 bg-gray-800 rounded-lg p-3 w-32 z-10">
                <Slider
                  vertical
                  value={volume * 100}
                  min={0}
                  max={100}
                  onChange={(value) => setVolume(value / 100)}
                  className="h-24"
                  tooltip={{ formatter: (value) => `${value}%` }}
                />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Tracks Modal */}
      <Modal
        title={
          <span className="text-gray-900 dark:text-stone-100">
            {"Available Tracks"}
          </span>
        }
        open={showTracksModal}
        onCancel={() => setShowTracksModal(false)}
        footer={null}
        width={500}
        className="tracks-modal [&_.ant-modal-content]:bg-white [&_.ant-modal-content]:dark:bg-stone-900 [&_.ant-modal-header]:bg-white [&_.ant-modal-header]:dark:bg-stone-900 [&_.ant-modal-header]:border-b [&_.ant-modal-header]:border-gray-200 [&_.ant-modal-header]:dark:border-stone-700 [&_.ant-modal-body]:bg-white [&_.ant-modal-body]:dark:bg-stone-900 [&_.ant-modal-body]:max-h-[70vh] [&_.ant-modal-body]:overflow-y-auto"
      >
        <List
          itemLayout="horizontal"
          dataSource={musicList}
          renderItem={(track, index) => (
            <List.Item
              className={`cursor-pointer hover:bg-gray-100 rounded-lg p-2 ${track.musicFile === currentMusic?.musicFile ? "bg-amber-50" : ""
                }`}
              onClick={() => handleTrackSelect(track)}
            >
              <List.Item.Meta
                avatar={
                  <Avatar
                    src={track.coverImage || "/placeholder.svg"}
                    shape="square"
                    size={48}
                  />
                }
                title={
                  <div className="flex items-center">
                    <span className="mr-2">{index + 1}.</span>
                    <span
                      className={
                        track.musicFile === currentMusic?.musicFile
                          ? "font-bold text-amber-800"
                          : ""
                      }
                    >
                      {track.title || `Track ${index + 1}`}
                    </span>
                    {track.musicFile === currentMusic?.musicFile && (
                      <Badge
                        status="processing"
                        color="orange"
                        className="ml-2"
                      />
                    )}
                  </div>
                }
                description={
                  <div>
                    <div>{track.artist || "Unknown Artist"}</div>
                    {track.duration && (
                      <div className="text-xs text-gray-500">
                        Duration: {track.duration}
                      </div>
                    )}
                  </div>
                }
              />
              {track.musicFile === currentMusic?.musicFile && isPlaying ? (
                <Button
                  icon={<PauseCircleOutlined />}
                  type="text"
                  onClick={(e) => {
                    e.stopPropagation();
                    togglePlay();
                  }}
                />
              ) : (
                <Button
                  icon={<PlayCircleOutlined />}
                  type="text"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (track.musicFile === currentMusic?.musicFile) {
                      togglePlay();
                    } else {
                      handleTrackSelect(track);
                    }
                  }}
                />
              )}
            </List.Item>
          )}
        />
      </Modal>

      {/* Read Modal */}
      <Modal
        title={
          <span className="text-gray-900 dark:text-stone-100">{`Read Audio`}</span>
        }
        open={showReadModal}
        onCancel={() => setShowReadModal(false)}
        footer={null}
        width={500}
        style={{ top: "20px", height: "98vh" }}
        className="tracks-modal 
    [&_.ant-modal-content]:bg-white 
    [&_.ant-modal-content]:dark:bg-stone-900 
    [&_.ant-modal-header]:bg-white 
    [&_.ant-modal-header]:dark:bg-stone-900 
    [&_.ant-modal-header]:border-b 
    [&_.ant-modal-header]:border-gray-200 
    [&_.ant-modal-header]:dark:border-stone-700 
    [&_.ant-modal-body]:bg-white 
    [&_.ant-modal-body]:dark:bg-stone-900 
    [&_.ant-modal-body]:max-h-[80vh] 
    [&_.ant-modal-body]:overflow-y-auto"
      >
        <div className="h-full overflow-y-auto">
          <p
            className="text-gray-900 dark:text-stone-100"
            dangerouslySetInnerHTML={{
              __html: currentMusic?.description || "",
            }}
          ></p>
        </div>
      </Modal>
    </div>
  );
};

export default AudioBookPlayerBigNew;
